import React from 'react';
import PropTypes from 'prop-types';

import { ActionBtn } from '@meronex/components';

import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';

import { useTheme } from '@material-ui/core/styles';
import IReverse from '@meronex/icons/fa/FaUndo';
import IEdit from '@meronex/icons/fa/FaEdit';
import ICopy from '@meronex/icons/fi/FiCopy';
import INote from '@meronex/icons/fd/FdClipboardNotes';
import IFactory from '@meronex/icons/mdc/MdcFactory';
import IInvoice from '@meronex/icons/fi/FiFileText';
import ITruck from '@meronex/icons/fi/FiTruck';
import IFolder from '@meronex/icons/fi/FiFolder';
import { toast } from 'react-toastify';
import { App } from '@meronex/app';

import storesMng from '../../../api/storesMng';
import TransactionItem from './TransactionItem';
import EditTransactionDialog from './EditTransactionDialog';
import { helpers } from '../../../../common/helpers';
import SitesAvatar from '../../../../common/SitesAvatar';
import SitesUserName from '../../../../common/SitesUserName';
import { can, p } from '../../../../common/roles';
import TopBar from '../../../../views/components/TopHeader';

import WorkOrderDialog from '../../../../factories/components/workorders/listWorkOrders/WorkOrderDialog';

const thousandsSeparator = App.utils.number.thousandsSeparator;
const round = App.utils.number.round;
const formatQuantity = (amount) => thousandsSeparator(round(amount));

function TransactionDialog(props) {
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';

  const {
    open,
    onClose,
    transaction,
    onReverse,
    permissions,
  } = props;

  const [reversing, setReversing] = React.useState(false);
  const [openWorkOrder, setOpenWorkOrder] = React.useState(false);
  const [openEdit, setOpenEdit] = React.useState(false);
  const [localTx, setLocalTx] = React.useState(transaction);

  React.useEffect(() => {
    setLocalTx(transaction);
  }, [transaction]);

  if (!localTx) {
    return null;
  }

  const storeItem = localTx.storeItem;
  const showPrice = can(p.stores.viewStoresPrices);

  const mutedColor = isDark ? 'rgba(255,255,255,0.45)' : '#94a3b8';
  const borderColor = isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9';
  const cardBg = isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc';

  const copyId = () => {
    try {
      navigator.clipboard.writeText(localTx.uniqid);
      toast.success(`${localTx.uniqid} copied.`, { autoClose: 900 });
    } catch (e) {
      toast.error('Failed to copy to clipboard.', { autoClose: 900 });
    }
  };

  const reverseTransaction = async () => {
    if (reversing) return;
    setReversing(true);
    try {
      await storesMng.reverseTransaction(localTx._id);
      toast.success('Transaction reversed successfully', { autoClose: 900 });
    } catch (e) {
      if (e.error === 'reverseTransaction.alreadyReversed') {
        toast.error(e.reason, { autoClose: 3000 });
      } else {
        toast.error('Ops, something went wrong');
      }
      console.error(e);
    } finally {
      if (onReverse) onReverse();
      setReversing(false);
      onClose();
    }
  };

  const isEditDisabled = () => {
    return localTx.reversed || !can(p.stores.itemIn);
  };

  const isReverseDisabled = () => {
    return (
      (localTx.from && localTx.from.isProduct) ||
      localTx.reversed ||
      (localTx.to && localTx.to.toProduct) ||
      localTx.transactionType === 'modify' ||
      !can(p.stores.reverseTransaction)
    );
  };

  // Transaction type label
  const TRANSACTION_TYPES = helpers.TRANSACTION_TYPES;
  const getTypeLabel = () => {
    const t = localTx.transactionType;
    if (t === TRANSACTION_TYPES.in) return localTx.workOrderId ? 'Product In' : 'In';
    if (t === TRANSACTION_TYPES.out) return 'Out';
    if (t === TRANSACTION_TYPES.transferIn) return 'Transfer In';
    if (t === TRANSACTION_TYPES.transferOut) return localTx.workOrderId ? 'Material Out' : 'Transfer Out';
    if (t === TRANSACTION_TYPES.reverse) return 'Reverse';
    if (t === TRANSACTION_TYPES.modify) return 'Modify';
    return t;
  };

  const hasMetadata = localTx.invoiceNumber || localTx.supplier || localTx.project;
  const hasNote = localTx.note;
  const hasWorkOrderProduct = localTx.to && localTx.to.toProduct;

  return (
    <Dialog
      open={open}
      className={'fullScreenMobile'}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: { borderRadius: '16px', overflow: 'hidden' },
      }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: '16px 20px',
        borderBottom: `1px solid ${borderColor}`,
      }}>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: '15px',
            fontWeight: 600,
            color: isDark ? '#e2e8f0' : '#0f172a',
          }}>
            Transaction Details
          </div>
          <div style={{
            fontSize: '12px',
            color: mutedColor,
            marginTop: 1,
          }}>
            {getTypeLabel()} · {App.utils.time.timeAgo(localTx.createdAt, true)}
          </div>
        </div>
        <div
          onClick={onClose}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            cursor: 'pointer',
            color: isDark ? '#94a3b8' : '#64748b',
            fontSize: '20px',
            transition: 'background-color 0.15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
          ✕
        </div>
      </div>

      <DialogContent style={{ padding: 0 }}>
        {/* Item header */}
        {storeItem && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            padding: '16px 20px',
            borderBottom: `1px solid ${borderColor}`,
          }}>
            <SitesAvatar
              image={storeItem.type.image}
              name={storeItem.type.name}
              size={56}
              round={14}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: '16px',
                fontWeight: 600,
                color: isDark ? '#e2e8f0' : '#0f172a',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {storeItem.type.name}
              </div>
              {storeItem.type.code && (
                <div style={{ fontSize: '12px', color: mutedColor, marginTop: 2 }}>
                  {storeItem.type.code}
                </div>
              )}
              <div style={{ fontSize: '12px', color: mutedColor, marginTop: 2 }}>
                {storeItem.type.unit} · Current stock: {formatQuantity(storeItem.quantity)}
              </div>
            </div>
          </div>
        )}

        {/* Transaction summary card */}
        <div style={{ padding: '16px 20px' }}>
          <TransactionItem item={localTx} showPrice={showPrice} isDark={isDark} />
        </div>

        {/* Work order product info */}
        {hasWorkOrderProduct && (
          <div style={{
            margin: '0 20px',
            padding: '12px 14px',
            borderRadius: '10px',
            backgroundColor: isDark ? 'rgba(99,102,241,0.06)' : '#eef2ff',
            border: `1px solid ${isDark ? 'rgba(99,102,241,0.12)' : 'rgba(99,102,241,0.1)'}`,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <SitesAvatar
              image={localTx.to.product?.image}
              name={localTx.to.product?.name}
              size={40}
              round={10}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '12px', color: mutedColor }}>
                <IFactory size={11} /> Used in production
              </div>
              <div style={{
                fontSize: '13px',
                fontWeight: 600,
                color: isDark ? '#e2e8f0' : '#0f172a',
                marginTop: 2,
              }}>
                {formatQuantity(localTx.to.product?.producedQuantity)} {localTx.to.product?.name}
              </div>
            </div>
          </div>
        )}

        {localTx.workOrder && (
          <div style={{ padding: '0 20px', marginTop: '10px' }}>
            <button
              onClick={() => setOpenWorkOrder(true)}
              style={{
                width: '100%',
                padding: '8px',
                fontSize: '13px',
                fontWeight: 500,
                color: isDark ? '#818cf8' : '#4f46e5',
                backgroundColor: 'transparent',
                border: `1px solid ${isDark ? 'rgba(129,140,248,0.2)' : 'rgba(79,70,229,0.2)'}`,
                borderRadius: '8px',
                cursor: 'pointer',
              }}>
              <IFactory size={12} /> Open Work Order
            </button>
          </div>
        )}

        {/* Details section */}
        <div style={{ padding: '0 20px', marginTop: '16px' }}>
          {/* Reverse info */}
          {localTx.reverseTarget && (
            <div style={{
              padding: '10px 14px',
              borderRadius: '8px',
              backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
              border: `1px solid ${borderColor}`,
              fontSize: '12px',
              lineHeight: 1.6,
              color: isDark ? 'rgba(255,255,255,0.6)' : '#475569',
              marginBottom: '10px',
            }}>
              <IReverse size={11} /> Reversing transaction <b>{localTx.reverseTarget.uniqid}</b> by{' '}
              <SitesUserName profile={localTx.reverseTarget.createdBy} /> ·{' '}
              {App.utils.time.timeAgo(localTx.reverseTarget.createdAt, true)}
            </div>
          )}

          {localTx.reversedBy && (
            <div style={{
              padding: '10px 14px',
              borderRadius: '8px',
              backgroundColor: isDark ? 'rgba(239,68,68,0.06)' : '#fef2f2',
              border: `1px solid ${isDark ? 'rgba(239,68,68,0.12)' : '#fecaca'}`,
              fontSize: '12px',
              lineHeight: 1.6,
              color: isDark ? '#f87171' : '#dc2626',
              marginBottom: '10px',
            }}>
              Reversed by <b>{localTx.reversedBy.uniqid}</b>
              {localTx.reversedBy.user && (
                <span> · {helpers.renderUserName(localTx.reversedBy.user)}</span>
              )}
              {' · '}{App.utils.time.timeAgo(localTx.reversedBy.createdAt)}
            </div>
          )}

          {/* Note */}
          {hasNote && (
            <div style={{
              display: 'flex',
              gap: '10px',
              padding: '10px 14px',
              borderRadius: '8px',
              backgroundColor: cardBg,
              border: `1px solid ${borderColor}`,
              marginBottom: '10px',
            }}>
              <INote size={14} style={{ color: mutedColor, flexShrink: 0, marginTop: 2 }} />
              <div style={{
                fontSize: '13px',
                lineHeight: 1.5,
                color: isDark ? '#e2e8f0' : '#1e293b',
              }}>
                {localTx.note}
              </div>
            </div>
          )}

          {/* Metadata */}
          {hasMetadata && (
            <div style={{
              padding: '10px 14px',
              borderRadius: '8px',
              backgroundColor: cardBg,
              border: `1px solid ${borderColor}`,
              marginBottom: '10px',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
            }}>
              {localTx.invoiceNumber && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                  <IInvoice size={13} style={{ color: mutedColor, flexShrink: 0 }} />
                  <span style={{ color: mutedColor }}>Invoice</span>
                  <span style={{ color: isDark ? '#e2e8f0' : '#1e293b', fontWeight: 500 }}>
                    {localTx.invoiceNumber}
                  </span>
                </div>
              )}
              {localTx.supplier && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                  <ITruck size={13} style={{ color: mutedColor, flexShrink: 0 }} />
                  <span style={{ color: mutedColor }}>Supplier</span>
                  <span style={{ color: isDark ? '#e2e8f0' : '#1e293b', fontWeight: 500 }}>
                    {localTx.supplier}
                  </span>
                </div>
              )}
              {localTx.project && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                  <IFolder size={13} style={{ color: mutedColor, flexShrink: 0 }} />
                  <span style={{ color: mutedColor }}>Project</span>
                  <span style={{ color: isDark ? '#e2e8f0' : '#1e293b', fontWeight: 500 }}>
                    {localTx.project}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Transaction ID */}
          {localTx.uniqid && (
            <div
              onClick={copyId}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 14px',
                borderRadius: '8px',
                backgroundColor: cardBg,
                border: `1px solid ${borderColor}`,
                cursor: 'pointer',
                marginBottom: '10px',
                transition: 'background-color 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc'; }}>
              <div>
                <div style={{ fontSize: '11px', color: mutedColor, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Transaction ID
                </div>
                <div style={{
                  fontSize: '13px',
                  fontWeight: 500,
                  color: isDark ? '#e2e8f0' : '#1e293b',
                  fontFamily: 'monospace',
                  marginTop: 2,
                }}>
                  {localTx.uniqid}
                </div>
              </div>
              <ICopy size={14} style={{ color: mutedColor }} />
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div style={{
          display: 'flex',
          gap: '10px',
          padding: '16px 20px 24px',
          justifyContent: 'center',
        }}>
          <ActionBtn
            onClick={() => setOpenEdit(true)}
            disabled={isEditDisabled()}
            label={
              <span>
                <IEdit /> Edit
              </span>
            }
            backgroundColor={'#1976d2'}
          />
          <ActionBtn
            onClick={reverseTransaction}
            loading={reversing}
            disabled={isReverseDisabled()}
            label={
              <span>
                <IReverse /> Reverse
              </span>
            }
            backgroundColor={'rgb(218 29 29)'}
          />
        </div>

        <EditTransactionDialog
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          transaction={localTx}
          onSave={(updated) => {
            setLocalTx((prev) => ({ ...prev, ...updated }));
            if (onReverse) onReverse();
          }}
        />
      </DialogContent>
      {localTx.workOrder && (
        <WorkOrderDialog
          workOrder={localTx.workOrder}
          open={openWorkOrder}
          readOnly={true}
          onClose={() => setOpenWorkOrder(false)}
        />
      )}
    </Dialog>
  );
}

TransactionDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  transaction: PropTypes.object,
  onReverse: PropTypes.func,
  permissions: PropTypes.object,
};

export default TransactionDialog;
